<template>
    <div class="password-page">
        <h2 class="page-title">密码管理</h2>
        <div class="info-section">
            <!-- 成功提示 -->
            <div class="success-toast" v-if="showSuccessToast">
                <div class="toast-content">
                    <i class="success-icon">✓</i>
                    <span class="success-text">{{ successMessage }}</span>
                </div>
            </div>

            <!-- 加载状态 -->
            <div class="loading-container" v-if="isLoading">
                <div class="loading-spinner"></div>
                <span class="loading-text">正在处理中...</span>
            </div>

            <!-- 未设置密码：去设置 -->
            <div class="password-form-card" v-else-if="!hasSetPassword">
                <div class="form-header">
                    <div class="form-title">设置登录密码</div>
                    <div class="form-desc">您尚未设置密码，请设置后使用</div>
                </div>

                <form class="password-form" @submit.prevent="handleSetPassword">
                    <div class="form-group">
                        <label class="form-label">新密码 <span class="required">*</span></label>
                        <input type="password" class="form-input" placeholder="请输入新密码" v-model="setForm.newPassword"
                            :disabled="isSubmitting" />
                        <div class="error-tip" v-if="errors.newPassword">{{ errors.newPassword }}</div>
                    </div>

                    <div class="form-group">
                        <label class="form-label">确认新密码 <span class="required">*</span></label>
                        <input type="password" class="form-input" placeholder="请再次输入新密码"
                            v-model="setForm.confirmPassword" :disabled="isSubmitting" />
                        <div class="error-tip" v-if="errors.confirmPassword">{{ errors.confirmPassword }}</div>
                    </div>

                    <div class="form-actions">
                        <button type="submit" class="btn-submit" :disabled="isSubmitting">
                            <span v-if="!isSubmitting">确认设置</span>
                            <span v-else>设置中...</span>
                        </button>
                    </div>
                </form>
            </div>

            <!-- 已设置密码：修改密码 / 忘记密码 -->
            <div class="password-form-card" v-else>
                <div class="form-header">
                    <div class="form-title">修改密码</div>
                    <div class="form-desc">请输入原密码完成修改，忘记密码可通过手机验证重置</div>
                </div>

                <!-- 正常修改密码表单 -->
                <form class="password-form" v-if="!showForgetForm" @submit.prevent="handleEditPassword">
                    <div class="form-group">
                        <label class="form-label">原密码 <span class="required">*</span></label>
                        <input type="password" class="form-input" placeholder="请输入原密码" v-model="editForm.oldPassword"
                            :disabled="isSubmitting" />
                        <div class="error-tip" v-if="errors.oldPassword">{{ errors.oldPassword }}</div>
                    </div>

                    <div class="form-group">
                        <label class="form-label">新密码 <span class="required">*</span></label>
                        <input type="password" class="form-input" placeholder="请输入新密码" v-model="editForm.newPassword"
                            :disabled="isSubmitting" />
                        <div class="error-tip" v-if="errors.newPassword">{{ errors.newPassword }}</div>
                    </div>

                    <div class="form-group">
                        <label class="form-label">确认新密码 <span class="required">*</span></label>
                        <input type="password" class="form-input" placeholder="请再次输入新密码"
                            v-model="editForm.confirmPassword" :disabled="isSubmitting" />
                        <div class="error-tip" v-if="errors.confirmPassword">{{ errors.confirmPassword }}</div>
                    </div>

                    <div class="forget-wrapper">
                        <a href="javascript:;" class="forget-link" @click="goToForget">忘记密码？</a>
                    </div>

                    <div class="form-actions">
                        <button type="submit" class="btn-submit" :disabled="isSubmitting">
                            <span v-if="!isSubmitting">确认修改</span>
                            <span v-else>修改中...</span>
                        </button>
                    </div>
                </form>

                <!-- 忘记密码 第一步：手机号+验证码 -->
                <form class="password-form" v-if="showForgetForm && forgetStep === 1">
                    <div class="form-group">
                        <label class="form-label">手机号 <span class="required">*</span></label>
                        <input type="text" class="form-input" placeholder="请输入绑定手机号" v-model="forgetForm.phone"
                            :disabled="isSubmitting || codeSending" />
                        <div class="error-tip" v-if="errors.phone">{{ errors.phone }}</div>
                    </div>

                    <div class="form-group code-group">
                        <label class="form-label">验证码 <span class="required">*</span></label>
                        <div class="code-wrapper">
                            <input type="text" class="form-input code-input" placeholder="请输入验证码"
                                v-model="forgetForm.code" />
                            <button type="button" class="code-btn" :disabled="codeDisabled || codeSending"
                                @click="sendCode">
                                {{ codeText }}
                            </button>
                        </div>
                        <div class="error-tip" v-if="errors.code">{{ errors.code }}</div>
                    </div>

                    <div class="form-actions">
                        <button type="button" class="btn-back" @click="backToEdit" :disabled="isSubmitting">
                            返回
                        </button>
                        <button type="button" class="btn-submit" :disabled="isSubmitting" @click="toSetPwdStep">
                            下一步
                        </button>
                    </div>
                </form>

                <!-- 忘记密码 第二步：设置新密码 -->
                <form class="password-form" v-if="showForgetForm && forgetStep === 2"
                    @submit.prevent="handleResetByPhone">
                    <div class="form-group">
                        <label class="form-label">新密码 <span class="required">*</span></label>
                        <input type="password" class="form-input" placeholder="请输入新密码" v-model="forgetForm.newPassword"
                            :disabled="isSubmitting" />
                        <div class="error-tip" v-if="errors.newPassword">{{ errors.newPassword }}</div>
                    </div>

                    <div class="form-group">
                        <label class="form-label">确认新密码 <span class="required">*</span></label>
                        <input type="password" class="form-input" placeholder="请再次输入新密码"
                            v-model="forgetForm.confirmPassword" :disabled="isSubmitting" />
                        <div class="error-tip" v-if="errors.confirmPassword">{{ errors.confirmPassword }}</div>
                    </div>

                    <div class="form-actions">
                        <button type="button" class="btn-back" @click="backToStepOne" :disabled="isSubmitting">
                            返回
                        </button>
                        <button type="submit" class="btn-submit" :disabled="isSubmitting">
                            <span v-if="!isSubmitting">确认重置</span>
                            <span v-else>重置中...</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PasswordPage',
    data() {
        return {
            // 状态
            isLoading: true,
            isSubmitting: false,
            hasSetPassword: false,
            showSuccessToast: false,
            successMessage: '',

            // 设置密码表单
            setForm: {
                newPassword: '',
                confirmPassword: '',
            },

            // 修改密码表单
            editForm: {
                oldPassword: '',
                newPassword: '',
                confirmPassword: '',
            },

            // 忘记密码 分步控制
            showForgetForm: false,
            forgetStep: 1, // 1=验证手机 2=设置新密码
            forgetForm: {
                phone: '',
                code: '',
                newPassword: '',
                confirmPassword: '',
            },

            // 验证码
            codeText: '获取验证码',
            codeDisabled: false,
            codeSending: false,
            codeTimer: null,

            // 错误
            errors: {},
        }
    },
    mounted() {
        this.checkPasswordStatus()
    },
    beforeDestroy() {
        if (this.codeTimer) clearInterval(this.codeTimer)
    },
    methods: {
        // 1. 查询是否设置过密码
        async checkPasswordStatus() {
            this.isLoading = true
            try {
                const res = await this.$apiFun.postPwd({})
                if (res.status === 200) {
                    this.hasSetPassword = !!res.obj.pwd
                } else {
                    this.$message.error('查询密码状态失败')
                }
            } catch (err) {
                console.error(err)
                this.$message.error('网络异常，请重试')
            } finally {
                this.isLoading = false
            }
        },

        // 2. 设置新密码（首次）
        async handleSetPassword() {
            if (!this.validateSetForm()) return
            this.isSubmitting = true
            try {
                const res = await this.$apiFun.postNewPwd({
                    NewPassword: this.setForm.newPassword,
                })
                if (res.status === 200) {
                    this.successMessage = '密码设置成功'
                    this.showSuccessToast = true
                    this.hasSetPassword = true
                    this.resetSetForm()
                    setTimeout(() => {
                        this.showSuccessToast = false
                    }, 3000)
                } else {
                    this.$message.error(res.msg || '设置失败')
                }
            } catch (err) {
                console.error(err)
                this.$message.error('设置失败，请重试')
            } finally {
                this.isSubmitting = false
            }
        },

        // 3. 修改密码
        async handleEditPassword() {
            if (!this.validateEditForm()) return
            this.isSubmitting = true
            try {
                const res = await this.$apiFun.postEditPwd({
                    UserPassword: this.editForm.oldPassword,
                    NewPassword: this.editForm.newPassword,
                })
                if (res.status === 200) {
                    this.successMessage = '密码修改成功'
                    this.showSuccessToast = true
                    this.resetEditForm()
                    setTimeout(() => {
                        this.showSuccessToast = false
                    }, 3000)
                } else {
                    this.$message.error(res.msg || '修改失败')
                }
            } catch (err) {
                console.error(err)
                this.$message.error('修改失败，请重试')
            } finally {
                this.isSubmitting = false
            }
        },

        // 4. 忘记密码 → 发送验证码
        async sendCode() {
            if (!this.forgetForm.phone) {
                this.errors.phone = '请输入手机号'
                return
            }
            this.codeSending = true
            try {
                const res = await this.$apiFun.getSendCodeV2s({
                    PhoneNumbers: this.forgetForm.phone,
                })
                if (res.status === 200) {
                    this.$message.success('验证码发送成功')
                    this.startCodeTimer()
                } else {
                    this.$message.error(res.msg || '发送失败')
                }
            } catch (err) {
                console.error(err)
                this.$message.error('发送失败，请重试')
            } finally {
                this.codeSending = false
            }
        },

        // 忘记密码 第一步 → 校验验证码 → 进入第二步
        async toSetPwdStep() {
            // 防重复点击
            if (this.isSubmitting) return
            this.isSubmitting = true

            try {
                // 1. 前端校验
                const valid = this.validateForgetStepOne()
                if (!valid) {
                    this.isSubmitting = false
                    return
                }

                // 2. 校验验证码接口（只调用一次）
                const res = await this.$apiFun.getverificationV3s({
                    code: this.forgetForm.code
                })

                if (res.status === 200) {
                    this.$message.success('验证码验证成功')
                    this.forgetStep = 2
                } else {
                    this.$message.error(res.msg || '验证码错误')
                }
            } catch (err) {
                console.error(err)
                this.$message.error('验证码校验失败')
            } finally {
                this.isSubmitting = false
            }
        },

        // 忘记密码 第二步 → 重置密码
        async handleResetByPhone() {
            if (!this.validateForgetStepTwo()) return
            this.isSubmitting = true
            try {
                const res = await this.$apiFun.postNewPwd({
                    NewPassword: this.forgetForm.newPassword,
                })
                if (res.status === 200) {
                    this.successMessage = '密码重置成功'
                    this.showSuccessToast = true
                    this.resetAllForget()
                    setTimeout(() => {
                        this.showSuccessToast = false
                    }, 3000)
                } else {
                    this.$message.error(res.msg || '重置失败')
                }
            } catch (err) {
                console.error(err)
                this.$message.error('重置失败，请重试')
            } finally {
                this.isSubmitting = false
            }
        },

        // 分步校验
        validateForgetStepOne() {
            const e = {}
            if (!this.forgetForm.phone) e.phone = '请输入手机号'
            if (!this.forgetForm.code) e.code = '请输入验证码'
            this.errors = e
            return Object.keys(e).length === 0
        },
        validateForgetStepTwo() {
            const e = {}
            if (!this.forgetForm.newPassword) e.newPassword = '请输入新密码'
            if (!this.forgetForm.confirmPassword) e.confirmPassword = '请确认密码'
            if (this.forgetForm.newPassword !== this.forgetForm.confirmPassword) {
                e.confirmPassword = '两次密码不一致'
            }
            this.errors = e
            return Object.keys(e).length === 0
        },

        // 其他校验
        validateSetForm() {
            const e = {}
            if (!this.setForm.newPassword) e.newPassword = '请输入新密码'
            if (!this.setForm.confirmPassword) e.confirmPassword = '请确认密码'
            if (this.setForm.newPassword !== this.setForm.confirmPassword) {
                e.confirmPassword = '两次密码不一致'
            }
            this.errors = e
            return Object.keys(e).length === 0
        },
        validateEditForm() {
            const e = {}
            if (!this.editForm.oldPassword) e.oldPassword = '请输入原密码'
            if (!this.editForm.newPassword) e.newPassword = '请输入新密码'
            if (!this.editForm.confirmPassword) e.confirmPassword = '请确认密码'
            if (this.editForm.newPassword !== this.editForm.confirmPassword) {
                e.confirmPassword = '两次密码不一致'
            }
            this.errors = e
            return Object.keys(e).length === 0
        },

        // 验证码倒计时
        startCodeTimer() {
            let s = 60
            this.codeDisabled = true
            this.codeText = `${s}秒后重新获取`
            this.codeTimer = setInterval(() => {
                s--
                this.codeText = `${s}秒后重新获取`
                if (s <= 0) {
                    clearInterval(this.codeTimer)
                    this.codeDisabled = false
                    this.codeText = '获取验证码'
                }
            }, 1000)
        },

        // 忘记密码 步骤返回
        backToEdit() {
            this.resetAllForget()
        },
       
        backToStepOne() {
            this.forgetStep = 1
            this.errors = {}
        },
        goToForget() {
            this.showForgetForm = true
            this.forgetStep = 1
            this.errors = {}
        },
        resetAllForget() {
            this.showForgetForm = false
            this.forgetStep = 1
            this.forgetForm = { phone: '', code: '', newPassword: '', confirmPassword: '' }
            this.errors = {}
            if (this.codeTimer) clearInterval(this.codeTimer)
            this.codeText = '获取验证码'
            this.codeDisabled = false
        },

        // 清空表单
        resetSetForm() {
            this.setForm = { newPassword: '', confirmPassword: '' }
        },
        resetEditForm() {
            this.editForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
        },
    },
}
</script>

<style lang="less" scoped>
// 完全沿用你参考代码的深色主题变量
@bg-primary: #1a1a1a;
@text-primary: #e0e0e0;
@text-secondary: #a0a0a0;
@accent-blue: #0ea5e9;
@accent-green: #0ea5e9;
@accent-gray: #888;
@border-color: #444;
@card-bg: #2c2c2c;
@input-bg: #333;
@error-color: #ff4d4f;
@modal-bg: rgba(0, 0, 0, 0.85);

.password-page {
    color: @text-primary;
    padding: 20px;
    box-sizing: border-box;
    position: relative;

    .page-title {
        font-size: 20px;
        font-weight: 500;
        margin-bottom: 20px;
        padding-bottom: 10px;
        border-bottom: 1px solid @border-color;
    }

    .info-section {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    // 成功提示
    .success-toast {
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 9999;
        background-color: rgba(34, 197, 94, 0.9);
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        animation: fadeInUp 0.3s ease;

        .toast-content {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .success-icon {
            font-size: 18px;
            font-weight: bold;
            display: inline-block;
            width: 20px;
            height: 20px;
            line-height: 20px;
            text-align: center;
            background: white;
            color: @accent-green;
            border-radius: 10%;
        }

        .success-text {
            font-size: 14px;
        }
    }

    // 加载
    .loading-container {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 40px;
        gap: 10px;

        .loading-spinner {
            width: 24px;
            height: 24px;
            border: 3px solid @accent-blue;
            border-top: 3px solid transparent;
            border-radius: 100%;
            animation: spin 1s linear infinite;
        }

        .loading-text {
            font-size: 14px;
            color: @text-secondary;
        }
    }

    // 卡片
    .password-form-card {
        background-color: @card-bg;
        border-radius: 8px;
        padding: 20px;
        box-sizing: border-box;
        border: 1px solid @border-color;
        max-width: 600px;

        .form-header {
            margin-bottom: 20px;

            .form-title {
                font-size: 18px;
                font-weight: 500;
                margin-bottom: 5px;
                color: @accent-blue;
            }

            .form-desc {
                font-size: 14px;
                color: @text-secondary;
            }
        }

        .password-form {
            display: flex;
            flex-direction: column;
            gap: 18px;
        }

        .form-group {
            display: flex;
            flex-direction: column;
            gap: 8px;

            .form-label {
                font-size: 14px;
                display: flex;
                align-items: center;

                .required {
                    color: @error-color;
                    margin-left: 3px;
                }
            }

            .form-input {
                width: 100%;
                padding: 10px 12px;
                background-color: @input-bg;
                border: 1px solid @border-color;
                border-radius: 4px;
                color: @text-primary;
                font-size: 14px;
                box-sizing: border-box;

                &:focus {
                    outline: none;
                    border-color: @accent-blue;
                }

                &:disabled {
                    background-color: #444;
                    cursor: not-allowed;
                    opacity: 0.8;
                }
            }

            .error-tip {
                font-size: 12px;
                color: @error-color;
                margin-top: -4px;
            }
        }

        // 验证码行
        .code-group {
            .code-wrapper {
                display: flex;
                gap: 10px;

                .code-input {
                    flex: 1;
                }

                .code-btn {
                    width: 120px;
                    background: @accent-blue;
                    color: #fff;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;

                    &:disabled {
                        background: #666;
                        cursor: not-allowed;
                    }
                }
            }
        }

        // 忘记密码链接
        .forget-wrapper {
            margin-top: -10px;
            text-align: right;

            .forget-link {
                color: @accent-blue;
                font-size: 13px;
                cursor: pointer;
            }
        }

        // 按钮组
        .form-actions {
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            margin-top: 10px;

            .btn-back {
                padding: 10px 20px;
                border-radius: 4px;
                font-size: 14px;
                cursor: pointer;
                transition: all 0.2s;
                border: 1px solid @border-color;
                background: transparent;
                color: @text-primary;

                &:hover {
                    border-color: @accent-blue;
                }

                &:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
            }

            .btn-submit {
                padding: 10px 20px;
                border-radius: 4px;
                font-size: 14px;
                cursor: pointer;
                transition: all 0.2s;
                border: none;
                background-color: @accent-blue;
                color: white;

                &:hover {
                    background-color: #0284c7;
                }

                &:disabled {
                    background-color: #666;
                    cursor: not-allowed;
                }
            }
        }
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translate(-50%, -20px);
    }

    to {
        opacity: 1;
        transform: translate(-50%, 0);
    }
}
</style>